import {
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  TextField,
  Tabs,
  Tab,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { VisibilityOff, Visibility, EmailOutlined, LockOutlined, Apple } from "@mui/icons-material";
import React, { useState, useEffect } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useLoginMutation, useResendOtpMutation } from "../../services/auth";
import { setAuth } from "../../reducers/authSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/store";
import { toast } from "react-toastify";
import { isValidPhoneNumber } from "libphonenumber-js";

import { clearSignupDraft } from "../../utils/authDraft";
import { formatOrSplitPhoneNumber } from "../../utils/profile";

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

function extractObjectError(data: any): string | null {
  if (!data || typeof data !== "object") return null;
  for (const key of Object.keys(data)) {
    const val = data[key];
    if (Array.isArray(val) && typeof val[0] === "string") return val[0];
    if (typeof val === "string" && key !== "code" && key !== "status") return val;
  }
  return null;
}

function getApiErrorMessage(err: any, fallback = "Invalid credentials") {
  const data = err?.data ?? err;
  if (typeof data === "string" && data.trim()) return data;

  const msg = data?.message || data?.detail || data?.error || err?.error || err?.message;
  if (typeof msg === "string" && msg.trim() && msg !== "Rejected") return msg;
  if (Array.isArray(msg) && msg[0]) return String(msg[0]);

  return extractObjectError(data) || fallback;
}

function isFailedLoginResponse(res: any) {
  const code = res?.code ?? res?.status_code ?? res?.statusCode;
  if (typeof code === "number" && code >= 400) return true;

  const token =
    res?.data?.access_token ||
    res?.access_token ||
    res?.data?.access ||
    res?.access ||
    res?.data?.token?.access ||
    res?.token?.access ||
    res?.data?.token ||
    res?.token;

  if (!token) return true;
  return false;
}

function formatLoginError(raw: string) {
  const msg = raw.toLowerCase();
  if (
    msg.includes("no active account") ||
    msg.includes("invalid credentials") ||
    msg.includes("incorrect") ||
    msg.includes("wrong password") ||
    msg.includes("unable to log in")
  ) {
    return "Invalid credentials";
  }
  if (msg.includes("not verified")) {
    return "User account is not available/verified.";
  }
  return raw;
}

const FacebookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#1877F2" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 12.07C24 5.41 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.04V9.41c0-3.02 1.8-4.7 4.54-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.5c-1.5 0-1.96.93-1.96 1.89v2.26h3.32l-.53 3.5h-2.8V24C19.62 23.1 24 18.1 24 12.07z" />
    <path d="M15.46 15.56l.53-3.5h-3.32v-2.26c0-.96.47-1.89 1.96-1.89h1.5V5.11s-1.37-.24-2.68-.24c-2.73 0-4.54 1.68-4.54 4.7v2.72H7.08v3.49h3.04V24c.62.1 1.25.15 1.88.15s1.27-.05 1.88-.15v-8.44h3.58z" fill="white" />
  </svg>
);

const Login = () => {
  const dispatch = useAppDispatch();
  const [login, { isLoading }] = useLoginMutation();
  const [resendOtp, { isLoading: isSendingOtp }] = useResendOtpMutation();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const token = useAppSelector(state => state.auth.token);

  useEffect(() => {
    if (token) {
      navigate("/dashboard");
    }
  }, [token, navigate]);

  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+971");
  const [nationalNumber, setNationalNumber] = useState("");
  const [errors, setErrors] = useState<{ email?: string; password?: string; phone?: string }>({});

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setErrors({});
  };

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handlePhoneLogin = async () => {
    let hasError = false;
    const newErrors: any = {};

    const split = formatOrSplitPhoneNumber(nationalNumber || phone, countryCode);
    const resolvedCountryCode = split.country_code || countryCode || "+91";
    const resolvedNationalNumber = split.phone_number.replace(/^\+/, "").replace(/\D/g, "");

    if (!resolvedNationalNumber) {
      newErrors.phone = "Phone number is required";
      hasError = true;
    } else {
      const fullWithPlus = `${resolvedCountryCode}${resolvedNationalNumber}`;
      if (!isValidPhoneNumber(fullWithPlus)) {
        newErrors.phone = "Please enter a valid phone number for the selected country";
        hasError = true;
      }
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    try {
      const payload = {
        country_code: resolvedCountryCode,
        phone_number: resolvedNationalNumber,
      };

      await resendOtp(payload).unwrap();
      toast.success("OTP sent successfully!");
      navigate("/otp", {
        state: {
          signupMethod: "phone",
          phone_number: resolvedNationalNumber,
          country_code: resolvedCountryCode,
          flow: "login",
        }
      });
    } catch (err: any) {
      console.error("Phone Login API Error:", err);
      toast.error(formatLoginError(getApiErrorMessage(err, "Unable to send OTP")));
    }
  };

  const handleEmailLogin = async (formData: FormData) => {
    const email = (formData.get("email") as string || "").toLowerCase().trim();
    const password = formData.get("password") as string;
    const remember = formData.get("remember") === "on";
    
    let hasError = false;
    const newErrors: any = {};
    if (!email) {
      newErrors.email = "Email is required";
      hasError = true;
    }
    if (!password) {
      newErrors.password = "Password is required";
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    try {
      const res = await login({ email, password }).unwrap();

      if (isFailedLoginResponse(res)) {
        toast.error(formatLoginError(getApiErrorMessage(res, "Invalid credentials")));
        return;
      }

      const token =
        res?.data?.access_token ||
        res?.access_token ||
        res?.data?.access ||
        res?.access ||
        res?.data?.token?.access ||
        res?.token?.access ||
        res?.data?.token ||
        res?.token;

      const userData = res?.data?.uuid ? res.data : (res?.data?.user || res?.user || {});

      if (remember) {
        localStorage.setItem("remembered_email", email);
        localStorage.setItem("remembered_password", password);
      } else {
        localStorage.removeItem("remembered_email");
        localStorage.removeItem("remembered_password");
      }

      clearSignupDraft();
      dispatch(setAuth({ token, userData }));
      toast.success(res?.message || "Logged in successfully");
      navigate("/dashboard");
    } catch (err: any) {
      console.error("Login API Error:", err);
      toast.error(formatLoginError(getApiErrorMessage(err, "Invalid credentials")));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});
    if (tabValue === 1) {
      await handlePhoneLogin();
    } else {
      await handleEmailLogin(new FormData(e.currentTarget));
    }
  };

  return (
    <AuthLayout>
      <div className="authBox">
        <h2>Log in to your account</h2>
        <p className="subtitle">Enter your email or phone number to continue</p>

        <div className="auth_tabs">
          <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
            <Tab label="Email" />
            <Tab label="Phone" />
          </Tabs>
        </div>

        <form className="form" onSubmit={handleSubmit}>
          {tabValue === 0 ? (
            <div className="control_group text_field">
              <label htmlFor="email">Email Address <span style={{ color: "red" }}>*</span></label>
              <TextField
                id="email"
                name="email"
                type="email"
                placeholder="Enter email address"
                fullWidth
                defaultValue={localStorage.getItem("remembered_email") || ""}
                onChange={(e) => { e.target.value = e.target.value.toLowerCase(); }}
                onBlur={(e) => { e.target.value = e.target.value.toLowerCase().trim(); }}
                error={!!errors.email}
                helperText={errors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlined style={{ color: "rgba(255, 255, 255, 0.4)" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          ) : (
            <div className="control_group">
              <label htmlFor="phone">Phone Number <span style={{ color: "red" }}>*</span></label>
              <PhoneInput
                country={"ae"}
                value={phone}
                countryCodeEditable={false}
                onChange={(val, data: any) => {
                  setPhone(val);

                  const dialCode = data?.dialCode ? String(data.dialCode) : "";
                  const selectedCc = dialCode ? `+${dialCode}` : countryCode || "+971";
                  const digits = (val || "").replace(/\D/g, "");

                  let national = "";
                  if (dialCode && digits.startsWith(dialCode)) {
                    national = digits.slice(dialCode.length);
                  } else if (digits) {
                    const split = formatOrSplitPhoneNumber(digits, selectedCc);
                    national = split.phone_number;
                  }
                  national = national.replace(/^\+/, "").replace(/\D/g, "");

                  setCountryCode(selectedCc);
                  setNationalNumber(national);

                  let errorMsg: string | undefined = undefined;
                  if (national) {
                    const fullWithPlus = `${selectedCc}${national}`;
                    if (!isValidPhoneNumber(fullWithPlus)) {
                      errorMsg = "Please enter a valid phone number for the selected country";
                    }
                  }
                  setErrors((prev: any) => ({ ...prev, phone: errorMsg }));
                }}
                placeholder="Enter number"
                enableSearch={true}
                inputStyle={errors.phone ? { border: "1px solid #d32f2f" } : {}}
              />
              {errors.phone && (
                <p className="MuiFormHelperText-root Mui-error css-1wc848c-MuiFormHelperText-root" style={{ margin: "3px 14px 0 14px" }}>
                  {errors.phone}
                </p>
              )}
            </div>
          )}

          {tabValue === 0 && (
            <>
              <div className="control_group text_field" style={{ marginTop: 15 }}>
                <label htmlFor="password">Password <span style={{ color: "red" }}>*</span></label>
                <TextField
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  fullWidth
                  defaultValue={localStorage.getItem("remembered_password") || ""}
                  error={!!errors.password}
                  helperText={errors.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined style={{ color: "rgba(255, 255, 255, 0.4)" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end" className="eye_btn">
                        <IconButton
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityOff style={{ color: "rgba(255, 255, 255, 0.4)" }} />
                          ) : (
                            <Visibility style={{ color: "rgba(255, 255, 255, 0.4)" }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>

              <div className="control_group d_flex rem_me">
                <div className="remember_box">
                  <FormControlLabel
                    control={<Checkbox defaultChecked={!!localStorage.getItem("remembered_email")} />}
                    name="remember"
                    label="Remember me"
                  />
                </div>
                <button
                  type="button"
                  className="anchor_link"
                  onClick={() => navigate("/forgot-password")}
                >
                  Forgot Password?
                </button>
              </div>
            </>
          )}

          <div className="form_btn">
            <Button
              type="submit"
              className="btn btn_primary w_100"
              disabled={isLoading || isSendingOtp}
              variant="contained">
              {isLoading || isSendingOtp ? "Logging in..." : "Log In"}
            </Button>
          </div>

          <div className="divider">or continue with</div>

          <div className="social_login">
            <Button variant="outlined" startIcon={<GoogleIcon />} onClick={() => window.open("https://google.com/", "_blank", "noopener,noreferrer")}>Google</Button>
            <Button variant="outlined" startIcon={<FacebookIcon />} onClick={() => window.open("https://www.facebook.com/login.php", "_blank", "noopener,noreferrer")}>Facebook</Button>
            <Button variant="outlined" startIcon={<Apple />} onClick={() => window.open("https://appleid.apple.com/", "_blank", "noopener,noreferrer")}>Apple</Button>
          </div>

          <div className="auth_bottom">
            <p>
              Don't have an account?{" "}
              <button
                type="button"
                className="link"
                onClick={() => navigate("/signup")}
              >
                Sign Up
              </button>
            </p>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
};

export default Login;
